/**
 * The Werks — custom header behaviour.
 *
 * Self-contained on purpose. Publishes --header-height / --header-group-height
 * itself, because the theme's own updateHeaderHeights() bails out when there is
 * no <header-component> on the page.
 */

const DESKTOP = '(min-width: 990px)';

class WerksHeader extends HTMLElement {
  connectedCallback() {
    this.desktop = window.matchMedia(DESKTOP);
    this.openItem = null;
    this.closeTimer = null;

    this.initMega();
    this.initDrawer();
    this.desktop.addEventListener('change', () => {
      this.closeMega();
      this.closeSuggest();
      this.removeAttribute('data-search-open');
    });
    this.initSearch();
    this.initCutoff();
    this.initSticky();
    this.publishHeights();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    window.clearInterval(this.cutoffTimer);
    window.clearInterval(this.placeholderTimer);
    document.removeEventListener('pointerdown', this.onDocPointer);
  }

  /* ---------------------------------------------------------- heights */

  publishHeights() {
    const write = () => {
      const height = Math.round(this.offsetHeight);
      document.body.style.setProperty('--werks-header-height', `${height}px`);
      // Other theme sections read these; keep them accurate.
      document.body.style.setProperty('--header-height', `${height}px`);
      document.body.style.setProperty('--header-group-height', `${height}px`);
    };

    write();
    this.resizeObserver = new ResizeObserver(write);
    this.resizeObserver.observe(this);
  }

  /* ---------------------------------------------------------- sticky */

  initSticky() {
    if (!this.classList.contains('werks-hdr--sticky')) return;

    /* position:sticky only travels within its containing block. This element
       sits inside a section wrapper that is exactly its own height, so sticking
       it here does nothing — it scrolls away with the page. The group wrapper
       spans the document, so that is what has to carry the sticky and the
       hide/reveal transform. */
    this.stickyRoot =
      this.closest('#header-group') || this.closest('.shopify-section') || this;
    this.stickyRoot.classList.add('werks-hdr-sticky-root');

    const reveal = this.dataset.scrollBehavior !== 'always';
    // Ignore sub-pixel jitter and iOS rubber-banding at the extremes.
    const DELTA = 6;
    const RELEASE = 120;

    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      ticking = false;
      const y = Math.max(window.scrollY, 0);
      const diff = y - lastY;

      this.toggleAttribute('data-scrolled', y > 4);

      if (!reveal) {
        lastY = y;
        return;
      }

      // Never hide while something anchored to the header is open, or the
      // user loses the thing they were interacting with.
      const busy =
        Boolean(this.dataset.megaOpen) ||
        this.hasAttribute('data-search-open') ||
        this.drawer?.hidden === false ||
        this.contains(document.activeElement);

      if (busy || y < RELEASE) {
        this.stickyRoot.removeAttribute('data-hidden');
        lastY = y;
        return;
      }

      if (Math.abs(diff) < DELTA) return;

      if (diff > 0) {
        this.stickyRoot.setAttribute('data-hidden', '');
        this.closeSuggest();
      } else {
        this.stickyRoot.removeAttribute('data-hidden');
      }

      lastY = y;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    update();
    document.addEventListener('scroll', onScroll, { passive: true });

    // Keyboard focus arriving in the header must bring it back.
    this.addEventListener('focusin', () => this.stickyRoot.removeAttribute('data-hidden'));
  }

  /* ---------------------------------------------------------- mega menu */

  initMega() {
    this.items = [...this.querySelectorAll('[data-werks-mega-item]')];

    this.items.forEach((item) => {
      const trigger = item.querySelector('[data-werks-mega-trigger]');
      const panel = item.querySelector('[data-werks-mega-panel]');
      if (!trigger || !panel) return;

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.openItem === item) {
          this.closeMega();
        } else {
          this.openMega(item);
        }
      });

      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' || !this.desktop.matches) return;
        window.clearTimeout(this.closeTimer);
        this.openMega(item);
      });

      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse' || !this.desktop.matches) return;
        this.closeTimer = window.setTimeout(() => this.closeMega(), 150);
      });
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (this.openItem) {
        const trigger = this.openItem.querySelector('[data-werks-mega-trigger]');
        this.closeMega();
        trigger?.focus();
      }
    });

    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.closeMega();
    });

    this.onDocPointer = (event) => {
      if (!this.contains(event.target)) {
        this.closeMega();
        this.closeSuggest();
      }
    };
    document.addEventListener('pointerdown', this.onDocPointer);

    this.initBrandFilter();
    this.initCategoryPanel();
  }

  openMega(item) {
    if (this.openItem === item) return;
    this.closeMega();

    item.querySelector('[data-werks-mega-panel]').hidden = false;
    item.querySelector('[data-werks-mega-trigger]').setAttribute('aria-expanded', 'true');
    item.dataset.open = 'true';
    this.dataset.megaOpen = 'true';
    this.openItem = item;
  }

  closeMega() {
    window.clearTimeout(this.closeTimer);
    if (!this.openItem) return;

    this.openItem.querySelector('[data-werks-mega-panel]').hidden = true;
    this.openItem.querySelector('[data-werks-mega-trigger]').setAttribute('aria-expanded', 'false');
    delete this.openItem.dataset.open;
    delete this.dataset.megaOpen;
    this.openItem = null;
  }

  initBrandFilter() {
    const scope = this.querySelector('[data-werks-brand-scope]');
    if (!scope) return;

    const input = scope.querySelector('[data-werks-brand-filter]');
    const rows = [...scope.querySelectorAll('[data-brand]')];
    const letters = [...scope.querySelectorAll('[data-brand-letter]')];
    const empty = scope.querySelector('[data-werks-brand-empty]');
    if (!input) return;

    input.addEventListener('input', () => {
      const term = input.value.trim().toLowerCase();
      let shown = 0;

      rows.forEach((row) => {
        const match = !term || row.dataset.brand.includes(term);
        row.hidden = !match;
        if (match) shown += 1;
      });

      // Letter headings only make sense in the unfiltered list.
      letters.forEach((letter) => {
        letter.hidden = Boolean(term);
      });

      if (empty) empty.hidden = shown !== 0;
    });
  }

  /* -------------------------------------------------------- categories */

  initCategoryPanel() {
    const scope = this.querySelector('[data-cat-scope]');
    if (!scope) return;

    const rows = [...scope.querySelectorAll('[data-cat-row]')];
    const groups = [...scope.querySelectorAll('[data-cat-group]')];
    const input = scope.querySelector('[data-cat-filter]');
    const empty = scope.querySelector('[data-cat-empty]');

    // Type-to-filter: at sixty-odd categories, scanning four columns is the
    // slow path. Hides empty groups too so the columns reflow tightly.
    input?.addEventListener('input', () => {
      const term = input.value.trim().toLowerCase();
      let shown = 0;

      rows.forEach((row) => {
        const match = !term || row.dataset.name.includes(term);
        row.hidden = !match;
        if (match) shown += 1;
      });

      groups.forEach((group) => {
        const visible = [...group.querySelectorAll('[data-cat-row]')].some((r) => !r.hidden);
        group.hidden = !visible;
      });

      if (empty) empty.hidden = shown !== 0;
    });

    this.initCategoryGarage(scope);
  }

  /**
   * Rewrites every category link to carry the visitor's saved chassis, so a
   * click lands on results already narrowed rather than the full catalog.
   * Opt-in, and remembered.
   */
  initCategoryGarage(scope) {
    const mount = scope.querySelector('[data-cat-garage]');
    const links = [...scope.querySelectorAll('[data-cat-link]')];
    if (!mount) return;

    const KEY = 'werks:garage';
    const PREF = 'werks:garage-filter';
    const filterKey = this.dataset.garageFilterKey || 'filter.p.tag';

    const readVehicle = () => {
      try {
        const raw = window.localStorage.getItem(KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (error) {
        return null;
      }
    };

    const readPref = () => {
      try {
        return window.localStorage.getItem(PREF) === 'on';
      } catch (error) {
        return false;
      }
    };

    const applyLinks = (vehicle, on) => {
      links.forEach((link) => {
        const base = link.dataset.baseHref;
        if (!base) return;

        if (!on || !vehicle) {
          link.href = base;
          return;
        }

        const separator = base.includes('?') ? '&' : '?';
        link.href = `${base}${separator}${filterKey}=${encodeURIComponent(vehicle.code)}`;
      });
    };

    const render = () => {
      const vehicle = readVehicle();

      if (!vehicle) {
        mount.hidden = true;
        mount.innerHTML = '';
        applyLinks(null, false);
        return;
      }

      const on = readPref();
      mount.hidden = false;
      mount.innerHTML = `
        <button type="button" aria-pressed="${on}" data-cat-garage-toggle>
          <span class="werks-hdr__cat-garage-dot" aria-hidden="true"></span>
          <span>Only ${vehicle.code} parts</span>
        </button>`;

      applyLinks(vehicle, on);

      mount.querySelector('[data-cat-garage-toggle]').addEventListener('click', () => {
        const next = !readPref();
        try {
          window.localStorage.setItem(PREF, next ? 'on' : 'off');
        } catch (error) {
          /* session-only is fine */
        }
        render();
      });
    };

    render();
    document.addEventListener('werks:garage', render);
  }

  /* ---------------------------------------------------------- drawer */

  initDrawer() {
    this.drawer = this.querySelector('[data-werks-drawer]');
    this.scrim = this.querySelector('[data-werks-scrim]');
    const openBtn = this.querySelector('[data-werks-drawer-open]');
    const closeBtn = this.querySelector('[data-werks-drawer-close]');
    if (!this.drawer || !openBtn) return;

    // Never inherit an open drawer from a cached render or the theme editor.
    this.toggleDrawer(false);

    // Resizing past the desktop breakpoint must not leave it stuck open.
    this.desktop.addEventListener('change', (event) => {
      if (event.matches) this.toggleDrawer(false);
    });

    openBtn.addEventListener('click', () => this.toggleDrawer(true));
    closeBtn?.addEventListener('click', () => this.toggleDrawer(false));
    this.scrim?.addEventListener('click', () => this.toggleDrawer(false));

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer.hidden === false) {
        this.toggleDrawer(false);
        openBtn.focus();
      }
    });
  }

  toggleDrawer(open) {
    this.drawer.hidden = !open;
    if (this.scrim) this.scrim.hidden = !open;
    this.querySelector('[data-werks-drawer-open]')?.setAttribute('aria-expanded', String(open));
    document.documentElement.classList.toggle('werks-drawer-open', open);
    if (open) this.querySelector('[data-werks-drawer-close]')?.focus();
  }

  /* ---------------------------------------------------------- search */

  initSearch() {
    this.searchWrap = this.querySelector('[data-werks-search]');
    this.input = this.querySelector('[data-werks-search-input]');
    this.suggest = this.querySelector('[data-werks-suggest]');
    const toggle = this.querySelector('[data-werks-search-toggle]');

    toggle?.addEventListener('click', () => {
      const open = this.toggleAttribute('data-search-open');
      if (open) this.input?.focus();
    });

    if (!this.input || !this.suggest) return;

    this.rotatePlaceholder();

    let controller = null;
    let debounce = null;

    this.input.addEventListener('input', () => {
      window.clearTimeout(debounce);
      const term = this.input.value.trim();

      if (term.length < 2) {
        this.closeSuggest();
        return;
      }

      debounce = window.setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();

        try {
          const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(
            term
          )}&resources[type]=product,collection&resources[limit]=6`;
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error('suggest failed');
          const data = await response.json();
          this.renderSuggest(data.resources?.results ?? {}, term);
        } catch (error) {
          if (error.name !== 'AbortError') this.closeSuggest();
        }
      }, 180);
    });

    this.input.addEventListener('focus', () => {
      if (this.suggest.childElementCount) this.openSuggest();
    });
  }

  renderSuggest(results, term) {
    const products = results.products ?? [];
    const collections = results.collections ?? [];

    if (!products.length && !collections.length) {
      this.closeSuggest();
      return;
    }

    // /search/suggest.json returns price already in currency units ("35.00"),
    // not cents. Format it with the shop's own money format so currency and
    // separators match the rest of the store.
    const money = (p) => {
      if (p.price === undefined || p.price === null || p.price === '') return '';

      const amount = Number(p.price);
      if (Number.isNaN(amount)) return String(p.price);

      const formatted = amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const template = this.dataset.moneyFormat;
      if (!template) return `$${formatted}`;

      if (/\{\{\s*amount_no_decimals[^}]*\}\}/.test(template)) {
        const whole = Math.round(amount).toLocaleString();
        return template.replace(/\{\{[^}]*\}\}/, whole);
      }

      return template.replace(/\{\{[^}]*\}\}/, formatted);
    };

    const productHtml = products
      .map(
        (p) => `
      <a class="werks-suggest__row" href="${p.url}">
        <span class="werks-suggest__thumb">${
          p.featured_image?.url ? `<img src="${p.featured_image.url}" alt="" loading="lazy">` : ''
        }</span>
        <span class="werks-suggest__body">
          <span class="werks-suggest__title">${p.title}</span>
          ${p.vendor ? `<span class="werks-suggest__vendor">${p.vendor}</span>` : ''}
        </span>
        <span class="werks-suggest__price">${money(p)}</span>
      </a>`
      )
      .join('');

    const collectionHtml = collections
      .map((c) => `<a class="werks-suggest__chip" href="${c.url}">${c.title}</a>`)
      .join('');

    this.suggest.innerHTML = `
      ${collectionHtml ? `<div class="werks-suggest__chips">${collectionHtml}</div>` : ''}
      ${productHtml ? `<div class="werks-suggest__rows">${productHtml}</div>` : ''}
      <a class="werks-suggest__all" href="${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(
        term
      )}&type=product">See all results for "${term}"</a>
    `;

    this.openSuggest();
  }

  openSuggest() {
    this.suggest.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  closeSuggest() {
    if (!this.suggest) return;
    this.suggest.hidden = true;
    this.input?.setAttribute('aria-expanded', 'false');
  }

  rotatePlaceholder() {
    const raw = this.input.dataset.rotatingPlaceholders || '';
    const options = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (options.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const base = this.input.placeholder;
    let index = 0;

    this.placeholderTimer = window.setInterval(() => {
      if (document.activeElement === this.input || this.input.value) {
        this.input.placeholder = base;
        return;
      }
      this.input.placeholder = `Try "${options[index]}"`;
      index = (index + 1) % options.length;
    }, 3200);
  }

  /* ---------------------------------------------------------- cutoff */

  initCutoff() {
    const target = this.querySelector('[data-werks-cutoff-text]');
    const wrap = this.querySelector('[data-werks-cutoff]');
    if (!target || !wrap) return;

    const cutoffHour = Number(this.dataset.cutoffHour || 14);
    const offset = Number(this.dataset.cutoffOffset || -4);
    const fallback = target.textContent.trim();

    const tick = () => {
      const now = new Date();
      const shopNow = new Date(now.getTime() + (offset * 60 + now.getTimezoneOffset()) * 60000);
      const day = shopNow.getDay();
      const weekend = day === 0 || day === 6;

      const cutoff = new Date(shopNow);
      cutoff.setHours(cutoffHour, 0, 0, 0);
      const msLeft = cutoff - shopNow;

      if (weekend || msLeft <= 0) {
        target.textContent = fallback;
        wrap.removeAttribute('data-live');
        return;
      }

      const hours = Math.floor(msLeft / 3600000);
      const minutes = Math.floor((msLeft % 3600000) / 60000);
      const left = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
      target.textContent = `Ships today if ordered in ${left}`;
      wrap.setAttribute('data-live', '');
    };

    tick();
    this.cutoffTimer = window.setInterval(tick, 30000);
  }
}

if (!customElements.get('werks-header')) {
  customElements.define('werks-header', WerksHeader);
}
